import React, { useEffect, useState } from "react";
import { FileText, ChevronDown, ChevronRight } from "lucide-react";
import { qaApi, RISK_META, RECOMMENDATION_META, formatTime } from "./api";

const ALL = "all";

const Reports = ({ onSelectCommit }) => {
  const [repos, setRepos] = useState([]);
  const [activeRepo, setActiveRepo] = useState(ALL);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  // Load the repo list once to build the tab bar.
  useEffect(() => {
    qaApi
      .listRepositories()
      .then((d) => setRepos(d.repositories || []))
      .catch(() => setRepos([]));
  }, []);

  // (Re)load reports whenever the active repo tab changes. The "all" tab omits
  // the filter; a specific tab passes repoId so the backend scopes the query.
  useEffect(() => {
    setLoading(true);
    setExpanded(null);
    const params = activeRepo === ALL ? { limit: 40 } : { limit: 40, repoId: activeRepo };
    qaApi
      .listReports(params)
      .then((d) => setReports(d.reports || []))
      .finally(() => setLoading(false));
  }, [activeRepo]);

  const tabs = [{ id: ALL, name: "All Repositories" }, ...repos.map((r) => ({ id: r._id, name: r.name }))];

  return (
    <div className="space-y-4">
      {/* Per-repository tabs */}
      {repos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tabs.map((t) => {
            const active = activeRepo === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveRepo(t.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${
                  active
                    ? "bg-primary-accent text-white border-primary-accent"
                    : "bg-white dark:bg-dark-300 text-gray-600 dark:text-light-400 border-gray-200 dark:border-dark-100 hover:bg-gray-50 dark:hover:bg-dark-400"
                }`}
              >
                {t.name}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-light-500">Loading reports…</p>
      ) : reports.length === 0 ? (
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-10 text-center">
          <FileText className="w-8 h-8 mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500 dark:text-light-500">
            {activeRepo === ALL ? "No QA reports yet." : "No QA reports for this repository yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => {
        const open = expanded === r._id;
        return (
          <div key={r._id} className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-dark-400 transition"
              onClick={() => setExpanded(open ? null : r._id)}
            >
              <div className="flex items-center gap-3 min-w-0">
                {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-light-100 truncate">
                    {activeRepo === ALL && r.repo?.name && (
                      <span className="px-1.5 py-0.5 mr-2 rounded bg-primary-accent/10 text-primary-accent text-[10px] font-semibold align-middle">
                        {r.repo.name}
                      </span>
                    )}
                    {r.prNumber ? `PR #${r.prNumber}` : "Commit report"} · {formatTime(r.createdAt)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-light-500 truncate">{r.aiNarrative}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RISK_META[r.riskLevel]?.className || ""}`}>
                  {r.riskLevel} {r.riskScore}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RECOMMENDATION_META[r.recommendation]?.className || ""}`}>
                  {RECOMMENDATION_META[r.recommendation]?.label || r.recommendation}
                </span>
              </div>
            </button>

            {open && (
              <div className="px-4 pb-4 space-y-3 text-sm border-t border-gray-100 dark:border-dark-100 pt-3">
                {r.aiNarrative && <p className="text-gray-700 dark:text-light-300 leading-relaxed">{r.aiNarrative}</p>}
                <Field label="Affected frontend" items={r.impactSummary?.affected_frontend} />
                <Field label="Affected endpoints" items={r.impactSummary?.affected_endpoints} />
                <Field label="Recommended tests" items={r.testPlan?.suites} />

                {/* Executed tests */}
                {r.testResults?.results?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-light-500 mb-1">Executed tests:</p>
                    <div className="space-y-1">
                      {r.testResults.results.map((t, i) => <ExecutedTest key={i} t={t} />)}
                    </div>
                  </div>
                )}

                {/* Merge decision / verdict */}
                {r.mergeDecision && (
                  <div className="p-3 rounded-xl bg-gray-50 dark:bg-dark-400">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-500 dark:text-light-500">Merge Decision:</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${VERDICT_TONE[r.mergeDecision.state] || ""}`}>
                        {VERDICT_LABEL[r.mergeDecision.state] || r.recommendation}
                      </span>
                    </div>
                    {(r.mergeDecision.reasons || []).length > 0 && (
                      <ul className="list-disc list-inside text-xs text-gray-600 dark:text-light-400">
                        {r.mergeDecision.reasons.map((reason, i) => <li key={i}>{reason}</li>)}
                      </ul>
                    )}
                  </div>
                )}

                {(r.bugRisks || []).length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-light-500 mb-1">Potential risks:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {r.bugRisks.map((b, i) => (
                        <li key={i} className="text-gray-600 dark:text-light-400">
                          <span className="font-medium">{b.severity} {b.type}</span> — {b.description}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {onSelectCommit && (
                  <button
                    className="text-xs text-primary-accent hover:underline"
                    onClick={() => onSelectCommit(r.commitId)}
                  >
                    Open live analysis →
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
        </div>
      )}
    </div>
  );
};

const VERDICT_TONE = {
  success: "bg-green-500/15 text-green-600 dark:text-green-300",
  failure: "bg-orange-500/15 text-orange-600 dark:text-orange-300",
  error: "bg-red-500/15 text-red-600 dark:text-red-300",
};
const VERDICT_LABEL = { success: "APPROVE", failure: "REQUEST_CHANGES", error: "BLOCK" };

const TEST_TONE = (status) =>
  status === "PASS"
    ? "text-green-600 dark:text-green-300"
    : status === "FAIL" || status === "ERROR"
      ? "text-red-600 dark:text-red-300"
      : "text-gray-500 dark:text-light-400";

const ExecutedTest = ({ t }) => {
  const mark = t.status === "PASS" ? "✔" : t.status === "FAIL" || t.status === "ERROR" ? "✖" : "○";
  const d = t.details || {};
  let detail = "";
  if (t.testType === "regression" && d.failedStep) detail = ` — ${d.failedStep}${d.actual ? ` (expected ${Array.isArray(d.expected) ? d.expected[0] : d.expected}, got ${d.actual})` : ""}`;
  else if (t.testType === "load" && d.p95) detail = ` (P95 ${d.p95}, errors ${d.errorRate})`;
  else if (t.breaking) detail = " — breaking";
  else if (t.status === "SKIPPED" && d.reason) detail = ` — ${d.reason}`;
  return (
    <div className={`flex items-center justify-between text-xs ${TEST_TONE(t.status)}`}>
      <span>
        {mark} <span className="capitalize">{t.testType}</span>: {t.status}
        <span className="text-gray-500 dark:text-light-500">{detail}</span>
      </span>
      {t.durationMs ? <span className="text-gray-400">{(t.durationMs / 1000).toFixed(1)}s</span> : null}
    </div>
  );
};

const Field = ({ label, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-gray-500 dark:text-light-500 w-36 shrink-0">{label}:</span>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span key={i} className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-dark-400 text-xs text-gray-700 dark:text-light-300">
            {it}
          </span>
        ))}
      </div>
    </div>
  );
};

export default Reports;
